import { X } from "lucide-react";

import { Button } from "../../utils/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../utils/components/ui/dialog";
import { Input } from "../../utils/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../utils/components/ui/select";

type BankingFormState = {
  referenceId: string;
  transactionDate: string;
  transactionType: string;
  mode: string;
  category: string;
  amount: string;
  remarks: string;
};

type AddBankingTransactionDialogProps = {
  open: boolean;
  formState: BankingFormState;
  isSubmitting?: boolean;
  transactionTypes: string[];
  modeOptions: string[];
  categoryOptions: Array<{
    label: string;
    value: string;
  }>;
  onOpenChange: (open: boolean) => void;
  onFieldChange: (field: keyof BankingFormState, value: string) => void;
  onSubmit: () => void;
};

function AddBankingTransactionDialog({
  open,
  formState,
  isSubmitting = false,
  transactionTypes,
  modeOptions,
  categoryOptions,
  onOpenChange,
  onFieldChange,
  onSubmit,
}: AddBankingTransactionDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-w-[860px] rounded-[28px] border border-[#E8EDF3] bg-white p-0 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.45)]"
      >
        <div className="p-6 sm:p-8">
          <DialogHeader className="flex-row items-start justify-between gap-4 text-left">
            <div>
              <DialogTitle className="text-3xl font-semibold tracking-tight text-[#1A5EF9]">
                New Transaction
              </DialogTitle>
              <DialogDescription className="mt-2 text-sm text-slate-500">
                Capture a banking entry with the same structured workflow used
                elsewhere in the system.
              </DialogDescription>
            </div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            >
              <span className="sr-only">Close</span>
              <X className="h-4 w-4" />
            </button>
          </DialogHeader>

          <div className="mt-8 grid gap-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600">
                Reference Number
              </label>
              <Input
                value={formState.referenceId}
                onChange={(event) =>
                  onFieldChange("referenceId", event.target.value)
                }
                placeholder="Enter"
                className="h-12 rounded-xl border-[#E6ECF5] bg-[#F8FAFC]"
              />
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600">
                  Transaction Date
                </label>
                <Input
                  type="date"
                  value={formState.transactionDate}
                  onChange={(event) =>
                    onFieldChange("transactionDate", event.target.value)
                  }
                  className="h-12 rounded-xl border-[#E6ECF5] bg-[#F8FAFC]"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600">
                  Mode
                </label>
                <Select
                  value={formState.mode}
                  onValueChange={(value) => onFieldChange("mode", value)}
                >
                  <SelectTrigger className="h-12 w-full rounded-xl border-[#E6ECF5] bg-[#F8FAFC]">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {modeOptions.map((mode) => (
                      <SelectItem key={mode} value={mode}>
                        {mode === "CASH" ? "Cash" : mode === "BANK" ? "Bank" : mode}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600">
                  Transaction Type
                </label>
                <Select
                  value={formState.transactionType}
                  disabled={!formState.mode}
                  onValueChange={(value) =>
                    onFieldChange("transactionType", value)
                  }
                >
                  <SelectTrigger className="h-12 w-full rounded-xl border-[#E6ECF5] bg-[#F8FAFC]">
                    <SelectValue
                      placeholder={
                        formState.mode ? "Select" : "Select mode first"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {transactionTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600">
                  Category
                </label>
                <Select
                  value={formState.category}
                  onValueChange={(value) => onFieldChange("category", value)}
                >
                  <SelectTrigger className="h-12 w-full rounded-xl border-[#E6ECF5] bg-[#F8FAFC]">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map((category) => (
                      <SelectItem key={category.value} value={category.value}>
                        {category.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600">
                Amount
              </label>
              <Input
                type="number"
                value={formState.amount}
                onChange={(event) =>
                  onFieldChange("amount", event.target.value)
                }
                placeholder="Enter"
                className="h-12 rounded-xl border-[#E6ECF5] bg-[#F8FAFC]"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600">
                Remarks
              </label>
              <textarea
                value={formState.remarks}
                onChange={(event) =>
                  onFieldChange("remarks", event.target.value)
                }
                placeholder="Enter"
                className="min-h-[120px] w-full rounded-xl border border-[#E6ECF5] bg-[#F8FAFC] px-3 py-3 text-sm text-slate-700 outline-none transition focus:border-[#D8E7B3] focus:ring-4 focus:ring-[#E8F4C9]"
              />
            </div>
          </div>

          <DialogFooter className="mt-8 border-t border-[#E8EDF3] pt-6 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="h-11 min-w-[112px] rounded-xl border-[#D8E1EC] px-6 text-slate-600"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={onSubmit}
              disabled={isSubmitting}
              className="h-11 min-w-[112px] rounded-xl bg-[#1A5EF9] px-6 text-white hover:bg-[#1A5EF9]/80"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AddBankingTransactionDialog;
